import { z } from "zod";

import { fails, reference, text } from "@frontend/lib/form/validators";
import { msg } from "@lingui/core/macro";

const optional = z.string().nullable();

export const phoneSchema = z.object({
  place: z.string().default(""),
  number: z
    .string()
    .trim()
    .min(1, fails(msg`Enter a number`))
    .default(""),
});

export const emailSchema = z.object({
  place: z.string().default(""),
  address: z
    .string()
    .trim()
    .pipe(z.email(fails(msg`Enter an email address`)))
    .default(""),
});

export const urlSchema = z.object({
  place: z.string().default(""),
  url: z
    .string()
    .trim()
    .pipe(z.url(fails(msg`Enter a link`)))
    .default(""),
});

export const addressSchema = z.object({
  typedAddressId: z.number().int().nullable().default(null),
  place: z.string().default(""),
  street: optional.default(null),
  extendedAddress: optional.default(null),
  postOfficeBox: optional.default(null),
  postalCode: optional.default(null),
  city: optional.default(null),
  state: optional.default(null),
  country: optional.default(null),
});

export const clientContactSchema = z.object({
  id: z.number().int().nullable().default(null),
  contactId: reference.default(null),
  typeId: reference.default(null),
});

export const accountantSchema = z.object({
  id: z.number().int().nullable().default(null),
  employeeId: reference.default(null),
  typeId: reference.default(null),
});

export const clientSchema = z
  .object({
    lockVersion: optional.default(null),
    contactId: z.number().int().nullable().default(null),
    title: optional.default(null),
    firstName: optional.default(null),
    lastName: optional.default(null),
    isOrganization: z.boolean().default(false),
    organization: optional.default(null),
    organizationSubName: optional.default(null),
    phones: z.array(phoneSchema).default([]),
    emails: z.array(emailSchema).default([]),
    urls: z.array(urlSchema).default([]),
    addresses: z.array(addressSchema).default([]),
    statusId: reference.default(null),
    typeId: reference.default(null),
    fieldId: reference.default(null),
    regionId: reference.default(null),
    provenanceId: reference.default(null),
    provenanceNote: optional.default(null),
    provenanceClientId: reference.default(null),
    provenanceEmployeeId: reference.default(null),
    comments: optional.default(null),
    contacts: z.array(clientContactSchema).default([]),
    accountants: z.array(accountantSchema).default([]),
  })
  .superRefine((values, ctx) => {
    const named = values.isOrganization ? values.organization : values.lastName;

    if (null === named || "" === named.trim()) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`Enter a name`),
        path: [values.isOrganization ? "organization" : "lastName"],
      });
    }
  });

export type PhoneValues = z.infer<typeof phoneSchema>;
export type EmailValues = z.infer<typeof emailSchema>;
export type UrlValues = z.infer<typeof urlSchema>;
export type AddressValues = z.infer<typeof addressSchema>;
export type ClientContactValues = z.infer<typeof clientContactSchema>;
export type AccountantValues = z.infer<typeof accountantSchema>;
export type ClientValues = z.infer<typeof clientSchema>;
